import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import * as Analytics from 'analytics/Analytics';
import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {AnalyticsMetadataForContext} from 'analytics/AnalyticsTypes';
import {ActionOrigin, COMMON_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import QueryErrorHandlingUnitOpLinkingEditor from 'studies/typed/shared/unit-op-linking-flowchart/QueryErrorHandlingUnitOpLinkingEditor';
import useUnitOpLinkingState, {
  UnitOpLinkingStateStatus,
} from 'studies/typed/shared/unit-op-linking-flowchart/useUnitOpLinkingState';
import Modal from 'ui-library/modals/Modal';

import {
  bulkSetProcedureMethodExecutionInstanceAssociations,
  mapEdgesToUpdates,
} from './AddUnitOperationLinksModalUtils';

export interface AddUnitOperationLinksModalProps {
  studyId: StrictString;
  procedureRunId: StrictString;
  procedureRunName: StrictString;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

export default function AddUnitOperationLinksModal(props: AddUnitOperationLinksModalProps): JSX.Element {
  const unitOpLinkingState = useUnitOpLinkingState(props.studyId, {procedureRunId: props.procedureRunId});
  const querySuccess = unitOpLinkingState.status === UnitOpLinkingStateStatus.SUCCESS;
  const analyticsMetadata: AnalyticsMetadataForContext = {
    actionOrigin: ActionOrigin.STUDY_RUN_DETAILS_PAGE,
    modalType: 'add_unit_operation_instance_links',
  };
  const analyticsModule = ANALYTICS_MODULE.STUDIES;
  function onSubmit(): void {
    if (querySuccess && unitOpLinkingState.newLinks != null) {
      // unitOpLinkingState.newLinks will be null if the user has not made any changes
      bulkSetProcedureMethodExecutionInstanceAssociations(
        mapEdgesToUpdates(unitOpLinkingState.newLinks, unitOpLinkingState.destinationPMEIs)
      ).then(() => {
        Analytics.trackEventWithModule(
          COMMON_ANALYTICS_ACTIONS.SUBMITTED_MODAL,
          analyticsModule,
          analyticsMetadata
        );
        props.onRequestSubmit();
      });
    } else {
      Analytics.trackEventWithModule(
        COMMON_ANALYTICS_ACTIONS.CLOSED_MODAL,
        analyticsModule,
        analyticsMetadata
      );
      props.onRequestSubmit();
    }
  }

  return (
    <Modal
      extraLarge={true}
      bodyPadded={false}
      title={
        <div className={css(styles.title)}>
          <span>Link to existing unit operations</span>
        </div>
      }
      onRequestClose={() => {
        Analytics.trackEventWithModule(
          COMMON_ANALYTICS_ACTIONS.CLOSED_MODAL,
          analyticsModule,
          analyticsMetadata
        );
        props.onRequestClose();
      }}
      onRequestSubmit={onSubmit}
      buttons={[
        {
          text: 'Cancel',
          cancel: true,
          disabled: false,
        },
        {
          text: 'Confirm',
          submit: true,
          disabled: !querySuccess,
          dataTestAttrs: {component: 'AddUnitOperationLinksModal', element: 'submitButton'},
        },
      ]}
    >
      <div
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
          }
        }}
      >
        <div className={css(styles.recipeRunTitle)}>
          <span>Recipe Run: {props.procedureRunName}</span>
        </div>
        <div className={css(styles.container)}>
          <QueryErrorHandlingUnitOpLinkingEditor unitOpLinkingState={unitOpLinkingState} />
        </div>
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  container: {
    height: '500px',
  },
  title: {
    font: 'Proxima Nova',
    fontSize: 18,
    fontWeight: 400,
    color: '#1b1b1f',
  },
  recipeRunTitle: {
    font: 'Proxima Nova',
    display: 'flex',
    fontSize: 13,
    fontWeight: 600,
    color: 'black',
    paddingTop: '6px',
    paddingRight: '16px',
    paddingLeft: '16px',
    paddingBottom: '6px',
    borderBottom: '1px solid #e2e3e5',
    gap: '4px',
  },
  loadingDiv: {
    paddingBottom: 5,
  },
});
